import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { selectAuthenticated, selectUserEmail } from "../../../store/slices/authUserSlice";
import instantNewsletterService, { StrategySummary } from "../../../services/instantNewsletterService";

interface ResultsStepProps {
  magicLink: string;
  editionId: number;
  newsletterName: string;
  sessionToken: string;
}

const FORMAT_LABELS: Record<string, string> = {
  short_original: "Short Original",
  long_original: "Long Form",
  short_summary: "Summary",
  link_dump: "Link Roundup",
  testimonials: "Testimonials",
  featured_job: "Featured Job",
};

const ResultsStep: React.FC<ResultsStepProps> = ({ magicLink, editionId, newsletterName, sessionToken }) => {
  const isAuthenticated = useSelector((state: RootState) => selectAuthenticated(state));
  const userEmail = useSelector((state: RootState) => selectUserEmail(state));
  const [strategyDoc, setStrategyDoc] = useState<{ url: string | null; summary: StrategySummary } | null>(null);
  const [loadingDoc, setLoadingDoc] = useState(true);

  // Email preview
  const [previewEmail, setPreviewEmail] = useState(userEmail || "");
  const [sendingPreview, setSendingPreview] = useState(false);
  const [previewSent, setPreviewSent] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionToken) {
      setLoadingDoc(false);
      return;
    }
    instantNewsletterService.createStrategyDoc(sessionToken).then((res) => {
      setStrategyDoc({ url: res.google_doc_url, summary: res.summary });
      setLoadingDoc(false);
    }).catch(() => {
      setLoadingDoc(false);
    });
  }, [sessionToken]);

  const handleSendPreview = async () => {
    if (!previewEmail.trim()) return;
    setSendingPreview(true);
    setPreviewError(null);
    try {
      await instantNewsletterService.sendPreview(sessionToken, editionId, previewEmail.trim());
      setPreviewSent(true);
    } catch (e: any) {
      setPreviewError(e.response?.data?.error || "Failed to send preview.");
    } finally {
      setSendingPreview(false);
    }
  };

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(previewEmail);

  return (
    <div style={{ textAlign: "center", maxWidth: "540px", width: "100%" }}>
      <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>✨</div>
      <h2 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>
        Your newsletter is ready!
      </h2>
      <p style={{ color: "#666", marginBottom: "1.5rem" }}>
        We've created a sample edition of <strong>{newsletterName}</strong>{isAuthenticated ? " in your account" : ""}.
      </p>

      <a
        href={magicLink}
        target="_blank"
        rel="noopener noreferrer"
        className="secondary-button"
        style={{
          display: "inline-block",
          padding: "14px 32px",
          fontSize: "1rem",
          textDecoration: "none",
          borderRadius: "8px",
          marginBottom: "1.5rem",
        }}
      >
        {isAuthenticated ? "Open in Editor →" : "View Your Newsletter →"}
      </a>

      {/* Optional email preview */}
      <div style={{
        background: "#f9f9f9",
        borderRadius: "10px",
        padding: "16px 20px",
        border: "1px solid #eee",
        marginBottom: "1.5rem",
        textAlign: "left",
      }}>
        <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "#888", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          Send a preview to your inbox
        </label>
        {previewSent ? (
          <p style={{ fontSize: "0.85rem", color: "#16a34a", margin: 0 }}>
            Preview sent to <strong>{previewEmail}</strong>
          </p>
        ) : (
          <div style={{ display: "flex", gap: "8px" }}>
            <input
              type="email"
              value={previewEmail}
              onChange={(e) => setPreviewEmail(e.target.value)}
              placeholder="you@company.com"
              style={{
                flex: 1,
                padding: "10px 12px",
                fontSize: "0.9rem",
                border: "1px solid #ddd",
                borderRadius: "8px",
                boxSizing: "border-box",
              }}
            />
            <button
              onClick={handleSendPreview}
              disabled={!isValidEmail || sendingPreview}
              style={{
                padding: "10px 16px",
                fontSize: "0.85rem",
                background: isValidEmail && !sendingPreview ? "#000" : "#ccc",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                cursor: isValidEmail && !sendingPreview ? "pointer" : "default",
                whiteSpace: "nowrap",
              }}
            >
              {sendingPreview ? "Sending..." : "Send"}
            </button>
          </div>
        )}
        {previewError && (
          <p style={{ fontSize: "0.8rem", color: "#dc2626", marginTop: "6px", marginBottom: 0 }}>{previewError}</p>
        )}
      </div>

      {/* Strategy Summary */}
      {strategyDoc && strategyDoc.summary && (
        <div style={{ textAlign: "left" }}>
          <div style={{
            background: "#f9f9f9",
            borderRadius: "12px",
            padding: "20px 24px",
            border: "1px solid #eee",
          }}>
            <h3 style={{ fontSize: "1.1rem", marginTop: 0, marginBottom: "12px" }}>
              Strategy Summary
            </h3>

            {strategyDoc.summary.newsletter_goal && (
              <div style={{ marginBottom: "12px" }}>
                <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px" }}>Goal</span>
                <p style={{ margin: "4px 0 0", fontSize: "0.9rem", color: "#444" }}>
                  {strategyDoc.summary.newsletter_goal}
                </p>
              </div>
            )}

            {strategyDoc.summary.icp && (
              <div style={{ marginBottom: "12px" }}>
                <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px" }}>Ideal Reader</span>
                <p style={{ margin: "4px 0 0", fontSize: "0.9rem", color: "#444" }}>
                  {strategyDoc.summary.icp}
                </p>
              </div>
            )}

            {strategyDoc.summary.sections && strategyDoc.summary.sections.length > 0 && (
              <div style={{ marginBottom: "12px" }}>
                <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px" }}>Sections</span>
                <div style={{ marginTop: "6px", display: "flex", flexDirection: "column", gap: "4px" }}>
                  {strategyDoc.summary.sections.map((s, i) => (
                    <div key={i} style={{ fontSize: "0.85rem", color: "#444", display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontWeight: 500 }}>{s.title}</span>
                      <span style={{
                        fontSize: "0.65rem",
                        background: "#e8e8e8",
                        padding: "1px 6px",
                        borderRadius: "3px",
                        color: "#666",
                      }}>
                        {FORMAT_LABELS[s.format] || s.format}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {strategyDoc.summary.cadence && (
              <div style={{ marginBottom: "12px" }}>
                <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#888", textTransform: "uppercase", letterSpacing: "0.5px" }}>Cadence</span>
                <p style={{ margin: "4px 0 0", fontSize: "0.9rem", color: "#444" }}>
                  {strategyDoc.summary.cadence.frequency}, {strategyDoc.summary.cadence.day_of_week}
                </p>
              </div>
            )}

            {strategyDoc.url && (
              <a
                href={strategyDoc.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  marginTop: "8px",
                  padding: "8px 16px",
                  background: "#fff",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  color: "#333",
                  textDecoration: "none",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                }}
              >
                View Full Strategy Document →
              </a>
            )}
          </div>
        </div>
      )}

      {loadingDoc && (
        <p style={{ color: "#999", fontSize: "0.8rem", marginTop: "1rem" }}>
          Generating your strategy document...
        </p>
      )}

      <p style={{ color: "#999", fontSize: "0.8rem", marginTop: "1.5rem" }}>
        This is a sample with placeholder content showing what your newsletter will look like.
      </p>
    </div>
  );
};

export default ResultsStep;
